/**
 * The one request-body builder (BENCH-V2-SPEC §9.2, §9.4): Standard and Batch send the same `params`, Standard adding only
 * `stream`. No `thinking`, `temperature`, `top_p` or `top_k` (the model's adaptive thinking is steered by `effort`).
 */
import type { CompletionRequest, CompletionResponse } from '../../engine/bench/types';
import { type AllowedModel, CACHE_MIN_TOKENS } from '../../shared/models';

/** `max_tokens` of a v2 request, and of the loop's single retry after `stop_reason: max_tokens`. */
export const DEFAULT_MAX_TOKENS = 32_000;
export const RETRY_MAX_TOKENS = 64_000;
/** The v1 truncation retry's ceiling (v1 requests carry no schema). */
export const MAX_TOKENS_CEILING_V1 = 32_000;

/** Prompt tokens, estimated as four characters each (only the cache decision uses it, and it is a lower-bound guard). */
export const systemTokensOf = (system: string): number => Math.ceil(system.length / 4);

export interface ParamsOptions {
	/** Provider-level switch: `false` never caches (default: cache when the request allows and the minimum is met). */
	cache?: boolean;
}

/**
 * The cache marker of a cacheable system prompt (BENCH-V2-SPEC §9.1, v3 ED-88): the one-hour TTL. A run lasts longer than the
 * default five minutes, and the 1-hour write (×2 of input) pays for itself after a few reads (×0.1).
 */
export const CACHE_CONTROL = { type: 'ephemeral', ttl: '1h' } as const;

export interface MessageParams {
	model: string;
	max_tokens: number;
	system:
		| string
		| { type: 'text'; text: string; cache_control: { type: 'ephemeral'; ttl: '1h' } }[];
	messages: { role: 'user'; content: string }[];
	output_config?: {
		effort?: string;
		format?: { type: 'json_schema'; schema: object };
	};
}

export function buildMessageParams(
	req: CompletionRequest,
	maxTokens: number,
	opts: ParamsOptions = {}
): MessageParams {
	const min = CACHE_MIN_TOKENS[req.model as AllowedModel];
	const cacheable =
		opts.cache !== false &&
		req.cacheSystem !== false &&
		min !== undefined &&
		systemTokensOf(req.system) >= min;
	const params: MessageParams = {
		model: req.model,
		max_tokens: maxTokens,
		system: cacheable
			? [{ type: 'text', text: req.system, cache_control: { ...CACHE_CONTROL } }]
			: req.system,
		messages: [{ role: 'user', content: req.user }],
	};
	if (req.effort || req.schema) {
		params.output_config = {
			...(req.effort ? { effort: req.effort } : {}),
			...(req.schema ? { format: { type: 'json_schema', schema: req.schema } } : {}),
		};
	}
	return params;
}

/** The object a schema request produced: the text parsed as JSON; undefined without a schema or for text that is not JSON. */
export function parsedOf(req: CompletionRequest, text: string): CompletionResponse['parsed'] {
	if (!req.schema) return undefined;
	try {
		return JSON.parse(text);
	} catch {
		return undefined;
	}
}
